import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Spinner,
  Dropdown,
  Row,
  Col,
  Container,
} from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useTasks from "hooks/tasks/useTasks";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import SubtleBadge from "components/common/SubtleBadge";
import DeleteTaskModal from "./DeleteTaskModal";
import DeactivateTaskModal from "./DeactivateTaskModal";
import { toast } from "react-toastify";

const Tasks = () => {
  const navigate = useNavigate();

  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  const [taskPendingDeactivate, setTaskPendingDeactivate] = useState(null);
  const [processing, setProcessing] = useState(false);

  const { tasks, loading, error, getTaskById, fetchTasks, deactivateTask, deleteTask } = useTasks();

  const formatID = (id) => `${id.slice(0, 8)}...`;
  const formatText = (text) => `${text.slice(0, 15)}...`;


  const mappedTasks = useMemo(() => {
    return tasks.map((task) => {
      
      const requester = task.requester;
      const helper = task.helper;

      const requesterName = requester
        ? `${requester.firstName || ""} ${requester.lastName || ""}`.trim().replace(/\s+/g, ' ')
        : "N/A";

      const helperName = helper
        ? `${helper.firstName || ""} ${helper.lastName || ""}`.trim().replace(/\s+/g, ' ')
        : "N/A";

      return {
        id: task.id,
        title: task.title,
        requesterName,
        helperName,
        status: task.status,
        totalAmount: task.taskCharges?.totalCharge || 0,
      };
    });
  }, [tasks]);


  const statusMap = {
    ACCEPTED: { label: "Accepted", color: "success" },
    NEW: { label: "New", color: "secondary" },
    PENDING: { label: "Pending", color: "warning" },
    NAVIGATING: { label: "Navigating", color: "info" },
    IN_PROGRESS: { label: "In Progress", color: "info" },
    COMPLETED: { label: "Completed", color: "primary" },
    CANCELLED: { label: "Cancelled", color: "danger" },
    EXPIRED: { label: "Expired", color: "secondary" },
    UNDECIDED: { label: "Undecided", color: "warning" },
  };

  // Updated triggerToast to handle both message and variant
  const triggerToast = useCallback((message, variant = "success") => {
    const toastMessage = message || (variant === "success" ? "Operation successful" : "Something went wrong");
    toast(toastMessage, {
      type: variant,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      autoClose: 3500,
      hideProgressBar: false,
      closeButton: true,
    });
  }, []);

  const handleViewTask = useCallback(
    async (id) => {
      try {
        await getTaskById(id);
        navigate(`/olitask/tasks/${id}`);
      } catch (err) {
        console.error("Error fetching task details:", err);
        triggerToast("Failed to fetch task details", "error");
      }
    },
    [getTaskById, navigate, triggerToast]
  );

  // Delete handlers
  const handleDeleteRequest = useCallback((task) => {
    setTaskPendingDelete(task);
  }, []);
  const handleCloseDeleteModal = useCallback(() => setTaskPendingDelete(null), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!taskPendingDelete) return;
    try {
      setProcessing(true);
      await deleteTask(taskPendingDelete.id);
      triggerToast("Task deleted successfully", "success");
      handleCloseDeleteModal();
      fetchTasks();
    } catch (err) {
      triggerToast(err?.message || "Failed to delete task", "error");
    } finally {
      setProcessing(false);
    }
  }, [deleteTask, taskPendingDelete, triggerToast, handleCloseDeleteModal, fetchTasks]);

  // Deactivate handlers
  const handleDeactivateRequest = useCallback((task) => {
    setTaskPendingDeactivate(task);
  }, []);

  const handleCloseDeactivateModal = useCallback(() => setTaskPendingDeactivate(null), []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!taskPendingDeactivate) return;
    try {
      setProcessing(true);
      await deactivateTask(taskPendingDeactivate.id);
      triggerToast("Task deactivated successfully", "success");
      handleCloseDeactivateModal();
      fetchTasks();
    } catch (err) {
      triggerToast(err?.message || "Failed to deactivate task", "error");
    } finally {
      setProcessing(false);
    }
  }, [deactivateTask, taskPendingDeactivate, triggerToast, handleCloseDeactivateModal, fetchTasks]);

  const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: "Task ID",
      cell: ({ getValue }) => formatID(getValue()),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ getValue }) => formatText(getValue()),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      accessorKey: "requesterName",
      header: "Requester",
      meta: { headerProps: { className: "text-900" } },
    },
    {
      accessorKey: "helperName",
      header: "Helper",
      meta: { headerProps: { className: "text-900" } },
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { headerProps: { className: "text-900" } },
      cell: ({ getValue }) => {
        const statusRaw = getValue() || "UNKNOWN";
        const { label, color } = statusMap[statusRaw] || { label: statusRaw, color: "secondary" };
        return <SubtleBadge pill bg={color}>{label}</SubtleBadge>;
      },
    },
    {
      accessorKey: "totalAmount",
      header: "Total Amount",
      cell: ({ getValue }) => `$${Number(getValue()).toFixed(2)}`,
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "actions",
      header: "Actions",
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Dropdown className="action-dropdown p-0">
            <Dropdown.Toggle
              as="button"
              className="bg-transparent border-0 p-0 shadow-none text-secondary"
            >
              ⋯
            </Dropdown.Toggle>

            <Dropdown.Menu className="action-menu">
              <Dropdown.Item onClick={() => handleViewTask(item.id)}>View</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => handleDeactivateRequest(item)}
                disabled={processing}
              >
                Deactivate
              </Dropdown.Item>
              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDeleteRequest(item)}
                disabled={processing}
              >
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ], [handleViewTask, handleDeactivateRequest, handleDeleteRequest, processing]);

  const table = useAdvanceTable({
    data: mappedTasks,
    columns,
    sortable: true,
    perPage: 10,
    pagination: true,
  });

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  if (loading && tasks.length === 0) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading tasks...</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger d-flex align-items-center justify-content-between">
          <div>Error loading tasks: {error}</div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Task Management"
        subtitle="Manage and oversee all tasks within the OliTask platform."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      {tasks.length === 0 && !loading ? (
        <div className="alert alert-info">No tasks found.</div>
      ) : (
        <AdvanceTableProvider {...table}>
          <Row className="mb-2">
            <Col md={4}>
              <AdvanceTableSearchBox />
            </Col>
          </Row>

          <AdvanceTable
            headerClassName="bg-300 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden",
            }}
          />

          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </AdvanceTableProvider>
      )}

      {/* Modals */}
      <DeleteTaskModal
        show={Boolean(taskPendingDelete)}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        task={taskPendingDelete}
        processing={processing}
      />

      <DeactivateTaskModal
        show={Boolean(taskPendingDeactivate)}
        onClose={handleCloseDeactivateModal}
        onConfirm={handleConfirmDeactivate}
        task={taskPendingDeactivate}
        processing={processing}
      />
    </>
  );
};

export default Tasks;
